import { useState, useEffect } from 'react';

interface Question {
  id: number;
  section: 'rw' | 'math';
  domain: string;
  subtopic: string;
  question: string;
  options: string[];
  answer: string;
  explanation: string;
  passage?: string;
}

interface FullExamQuestionCardProps {
  question: Question;
  questionNumber: number;
  userAnswer: string | null;
  onAnswer: (answer: string) => void;
  showSolution: boolean;
}

export default function FullExamQuestionCard({
  question,
  questionNumber,
  userAnswer,
  onAnswer,
  showSolution,
}: FullExamQuestionCardProps) {
  const [selectedOption, setSelectedOption] = useState<string | null>(userAnswer);

  useEffect(() => {
    setSelectedOption(userAnswer);
  }, [userAnswer, question.id]);

  const handleOptionClick = (option: string) => {
    if (showSolution) return;
    setSelectedOption(option);
    onAnswer(option);
  };

  const getOptionClass = (option: string) => {
    if (showSolution) {
      if (option === question.answer) {
        return 'bg-emerald-100 border-emerald-500 text-emerald-900';
      }
      if (option === userAnswer && option !== question.answer) {
        return 'bg-red-100 border-red-500 text-red-900';
      }
      return 'bg-slate-50 border-slate-200 text-slate-400';
    }

    if (option === selectedOption) {
      return 'bg-indigo-100 border-indigo-500 text-indigo-900';
    }
    return 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50';
  };

  const getDomainColor = () => {
    const colors: Record<string, string> = {
      'Information & Ideas': 'bg-blue-100 text-blue-700',
      'Craft & Structure': 'bg-purple-100 text-purple-700',
      'Expression of Ideas': 'bg-pink-100 text-pink-700',
      'Standard English Conventions': 'bg-rose-100 text-rose-700',
      'Algebra': 'bg-emerald-100 text-emerald-700',
      'Advanced Math': 'bg-cyan-100 text-cyan-700',
      'Problem-Solving & Data Analysis': 'bg-amber-100 text-amber-700',
      'Geometry & Trigonometry': 'bg-violet-100 text-violet-700',
    };
    return colors[question.domain] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-8">
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500 to-sky-500 flex items-center justify-center">
            <span className="text-white font-bold text-base">{questionNumber}</span>
          </div>
          <div>
            <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getDomainColor()}`}>
              {question.domain}
            </div>
            <div className="text-xs text-slate-500 mt-1">{question.subtopic}</div>
          </div>
        </div>
        <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">
          {question.section === 'rw' ? 'Reading & Writing' : 'Math'}
        </div>
      </div>

      {question.passage && (
        <div className="mb-6 p-5 bg-slate-50 rounded-lg border border-slate-200">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Reading Passage</div>
          <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{question.passage}</div>
        </div>
      )}

      <div className="mb-6">
        <p className="text-base text-slate-800 leading-relaxed whitespace-pre-line">{question.question}</p>
      </div>

      <div className="space-y-3 mb-6">
        {question.options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleOptionClick(option)}
            disabled={showSolution}
            className={`w-full text-left px-5 py-4 rounded-lg border-2 transition-all ${getOptionClass(option)} ${
              showSolution ? 'cursor-default' : 'cursor-pointer'
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="font-semibold text-sm mt-0.5">{String.fromCharCode(65 + index)}.</span>
              <span className="text-sm flex-1">{option}</span>
              {showSolution && option === question.answer && (
                <i className="ri-check-line text-lg text-emerald-600"></i>
              )}
              {showSolution && option === userAnswer && option !== question.answer && (
                <i className="ri-close-line text-lg text-red-600"></i>
              )}
            </div>
          </button>
        ))}
      </div>

      {showSolution && (
        <div className="mt-6 p-5 bg-indigo-50 rounded-lg border border-indigo-200">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-lightbulb-line text-lg text-indigo-600"></i>
            <span className="text-sm font-semibold text-indigo-900">Step-by-Step Solution</span>
          </div>
          <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{question.explanation}</div>
        </div>
      )}
    </div>
  );
}